import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { ArrowLeft, ExternalLink, Trash2, Plus, Save, Pencil, X, Wand2, Target, Sparkles, Search, Check, Users } from "lucide-react";
import ColorPicker from "@/components/ColorPicker";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { RegionBadge } from "@/components/RegionBadge";
import CompetitorRadar from "@/components/CompetitorRadar";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
};

const PIE_COLORS = ["#F59E0B", "#D97706", "#92400E", "#FCD34D", "#FBBF24", "#78350F"];

function EditableField({ label, value, onSave, multiline, testid }) {
    const [editing, setEditing] = useState(false);
    const [v, setV] = useState(value || "");
    useEffect(() => setV(value || ""), [value]);
    const commit = () => { setEditing(false); if (v !== value) onSave(v); };
    return (
        <div className="group">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 flex items-center gap-2">
                {label}
                {!editing && (
                    <button onClick={() => setEditing(true)} className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-600 hover:text-amber-500" data-testid={`${testid}-edit`}>
                        <Pencil className="w-3 h-3" />
                    </button>
                )}
            </div>
            {editing ? (
                multiline ? (
                    <textarea
                        autoFocus
                        value={v}
                        onChange={(e) => setV(e.target.value)}
                        onBlur={commit}
                        rows={4}
                        data-testid={`${testid}-input`}
                        className="w-full bg-zinc-950 border border-amber-500 px-3 py-2 text-sm text-zinc-100 focus:outline-none font-sans"
                    />
                ) : (
                    <input
                        autoFocus
                        value={v}
                        onChange={(e) => setV(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => e.key === "Enter" && commit()}
                        data-testid={`${testid}-input`}
                        className="w-full bg-zinc-950 border border-amber-500 px-3 py-2 text-sm text-zinc-100 focus:outline-none font-mono"
                    />
                )
            ) : (
                <div onClick={() => setEditing(true)} data-testid={testid} className={`text-sm text-zinc-200 cursor-text border-l-2 border-transparent hover:border-zinc-700 pl-2 -ml-2 ${multiline ? "leading-relaxed" : "font-mono"}`}>
                    {value || <span className="text-zinc-600">— click to edit</span>}
                </div>
            )}
        </div>
    );
}

function InlineEdit({ value, onSave, testid }) {
    const [editing, setEditing] = useState(false);
    const [v, setV] = useState(value || "");
    useEffect(() => setV(value || ""), [value]);
    const commit = () => { setEditing(false); if (v !== value) onSave(v); };
    if (editing) {
        return (
            <input
                autoFocus
                value={v}
                onChange={(e) => setV(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => e.key === "Enter" && commit()}
                data-testid={`${testid}-input`}
                className="w-full bg-zinc-950 border border-amber-500 px-2 py-1 text-sm text-zinc-100 focus:outline-none font-mono"
            />
        );
    }
    return (
        <span data-testid={testid} onClick={() => setEditing(true)} className="cursor-text inline-flex items-center gap-1.5 group">
            {value || <span className="text-zinc-600">—</span>}
            <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 text-zinc-600 transition-opacity" />
        </span>
    );
}

const blankProduct = {
    name: "New product",
    description: "",
    pricing: "",
    annual_revenue_usd: 0,
    units_sold: 0,
    growth_pct: 0,
    revenue_share_pct: 0,
    monthly_revenue: [],
};

export default function FirmDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [firm, setFirm] = useState(null);
    const [activeProductId, setActiveProductId] = useState(null);
    const [productDraft, setProductDraft] = useState(null);
    const [showProductModal, setShowProductModal] = useState(false);
    const [autoTagging, setAutoTagging] = useState(false);

    const load = () => api.get(`/firms/${id}`).then(({ data }) => {
        setFirm(data);
        if (!activeProductId && data.products?.length) setActiveProductId(data.products[0].id);
    });

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const updateFirm = async (patch) => {
        const { data } = await api.put(`/firms/${id}`, patch);
        setFirm(data);
    };

    const deleteFirm = async () => {
        if (!window.confirm("Delete this firm and all its products?")) return;
        await api.delete(`/firms/${id}`);
        nav("/app/library");
    };

    const autoTagRegion = async () => {
        setAutoTagging(true);
        try {
            const { data } = await api.post(`/firms/${id}/auto-region`);
            setFirm(data);
        } finally {
            setAutoTagging(false);
        }
    };

    const openAddProduct = () => { setProductDraft({ ...blankProduct, id: null }); setShowProductModal(true); };
    const openEditProduct = (p) => { setProductDraft({ ...p }); setShowProductModal(true); };
    const saveProduct = async () => {
        const payload = {
            name: productDraft.name,
            description: productDraft.description || "",
            pricing: productDraft.pricing || "",
            annual_revenue_usd: Number(productDraft.annual_revenue_usd) || 0,
            units_sold: Number(productDraft.units_sold) || 0,
            growth_pct: Number(productDraft.growth_pct) || 0,
            revenue_share_pct: Number(productDraft.revenue_share_pct) || 0,
            monthly_revenue: productDraft.monthly_revenue || [],
        };
        let data;
        if (productDraft.id) {
            ({ data } = await api.put(`/firms/${id}/products/${productDraft.id}`, payload));
        } else {
            ({ data } = await api.post(`/firms/${id}/products`, payload));
        }
        setFirm(data);
        setShowProductModal(false);
    };
    const deleteProduct = async (pid) => {
        if (!window.confirm("Delete this product?")) return;
        const { data } = await api.delete(`/firms/${id}/products/${pid}`);
        setFirm(data);
        if (activeProductId === pid) setActiveProductId(data.products?.[0]?.id || null);
    };

    const activeProduct = useMemo(() => firm?.products?.find((p) => p.id === activeProductId) || firm?.products?.[0], [firm, activeProductId]);
    const totalRevenue = useMemo(() => (firm?.products || []).reduce((s, p) => s + (Number(p.annual_revenue_usd) || 0), 0), [firm]);
    const pieData = useMemo(() => (firm?.products || []).map((p) => ({ name: p.name, value: Number(p.annual_revenue_usd) || 0 })), [firm]);

    if (!firm) return <div className="p-12"><Loader label="Loading firm" /></div>;

    return (
        <div className="p-8" data-testid="firm-detail-page">
            <div className="mb-6 flex items-center justify-between">
                <Link to="/app/library" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-amber-500" data-testid="back-to-library">
                    <ArrowLeft className="w-3.5 h-3.5" /> Library
                </Link>
                <div className="flex items-center gap-2">
                    <Link
                        to={`/app/firms/${firm.id}/partners`}
                        data-testid="find-partners-btn"
                        className="inline-flex items-center gap-2 border border-amber-500/60 hover:border-amber-400 bg-amber-500/10 hover:bg-amber-500/20 text-xs font-mono uppercase tracking-widest text-amber-400 px-3 py-2 transition-colors"
                        title="Score every firm in your library against this one"
                    >
                        <Target className="w-3.5 h-3.5" /> Find partners
                    </Link>
                    <Link
                        to={`/app/firms/${firm.id}/stretch`}
                        data-testid="stretch-partners-btn"
                        className="inline-flex items-center gap-2 border border-emerald-500/60 hover:border-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-mono uppercase tracking-widest text-emerald-300 px-3 py-2 transition-colors"
                        title="Find companies that would become great partners if you make specific changes"
                    >
                        <Sparkles className="w-3.5 h-3.5" /> Stretch partners
                    </Link>
                    <Link
                        to={`/app/firms/${firm.id}/stakeholders`}
                        data-testid="stakeholders-btn"
                        className="inline-flex items-center gap-2 border border-blue-500/60 hover:border-blue-400 bg-blue-500/10 hover:bg-blue-500/20 text-xs font-mono uppercase tracking-widest text-blue-300 px-3 py-2 transition-colors"
                        title="Map the 5 key roles you need to engage at this firm"
                    >
                        <Users className="w-3.5 h-3.5" /> Stakeholders
                    </Link>
                    <button onClick={deleteFirm} data-testid="delete-firm-btn" className="inline-flex items-center gap-2 border border-zinc-800 hover:border-red-700 hover:text-red-400 text-xs font-mono uppercase tracking-widest text-zinc-500 px-3 py-2 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Delete firm
                    </button>
                </div>
            </div>

            {/* Header */}
            <header
                className="border border-zinc-800 bg-zinc-950 p-6 mb-6 border-l-4"
                style={firm.color ? { borderLeftColor: firm.color } : { borderLeftColor: "#27272A" }}
                data-testid="firm-header"
            >
                <div className="flex items-start justify-between gap-6 mb-5">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">// Firm Profile</div>
                            {firm.region && firm.region !== "Other" && <RegionBadge region={firm.region} />}
                            {(firm.themes || []).map((t) => (
                                <span key={t} data-testid={`theme-${t}`} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300">{t}</span>
                            ))}
                            <ColorPicker
                                value={firm.color || ""}
                                onChange={(c) => updateFirm({ color: c })}
                                testid="firm-color-picker"
                            />
                            <button
                                onClick={autoTagRegion}
                                disabled={autoTagging}
                                data-testid="auto-region-btn"
                                title="Ask AI to detect the region for this firm"
                                className="inline-flex items-center gap-1 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-[10px] font-mono uppercase tracking-wider text-zinc-400 px-2 py-0.5 transition-colors disabled:opacity-50"
                            >
                                <Wand2 className="w-3 h-3" /> {autoTagging ? "Detecting…" : (firm.region && firm.region !== "Other" ? "Re-tag region" : "Auto-tag region")}
                            </button>
                        </div>
                        <EditableField label="Name" value={firm.name} onSave={(v) => updateFirm({ name: v })} testid="firm-name" />
                    </div>
                    <div className="text-right shrink-0">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Total revenue</div>
                        <div className="font-heading font-black text-3xl text-amber-500">{fmtMoney(totalRevenue)}</div>
                    </div>
                </div>

                {/* Key facts — compact data table */}
                <div className="overflow-x-auto border border-zinc-800 mt-2" data-testid="firm-key-facts-table">
                    <table className="w-full text-sm">
                        <tbody className="font-mono">
                            <tr className="border-b border-zinc-800/60">
                                <th className="text-left px-3 py-2 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500 w-32">Industry</th>
                                <td className="px-3 py-2 text-zinc-200">
                                    <InlineEdit value={firm.industry} onSave={(v) => updateFirm({ industry: v })} testid="firm-industry-cell" />
                                </td>
                                <th className="text-left px-3 py-2 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500 w-32">Headquarters</th>
                                <td className="px-3 py-2 text-zinc-200">
                                    <InlineEdit value={firm.headquarters} onSave={(v) => updateFirm({ headquarters: v })} testid="firm-hq-cell" />
                                </td>
                            </tr>
                            <tr className="border-b border-zinc-800/60">
                                <th className="text-left px-3 py-2 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Founded</th>
                                <td className="px-3 py-2 text-zinc-200">
                                    <InlineEdit value={firm.founded_year ? String(firm.founded_year) : ""} onSave={(v) => updateFirm({ founded_year: parseInt(v) || null })} testid="firm-founded-cell" />
                                </td>
                                <th className="text-left px-3 py-2 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Company size</th>
                                <td className="px-3 py-2 text-zinc-200">
                                    <InlineEdit value={firm.company_size} onSave={(v) => updateFirm({ company_size: v })} testid="firm-size-cell" />
                                </td>
                            </tr>
                            <tr className="border-b border-zinc-800/60">
                                <th className="text-left px-3 py-2 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Region</th>
                                <td className="px-3 py-2 text-zinc-200">{firm.region || "—"}</td>
                                <th className="text-left px-3 py-2 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500"># Products</th>
                                <td className="px-3 py-2 text-amber-500">{(firm.products || []).length}</td>
                            </tr>
                            {firm.website && (
                                <tr>
                                    <th className="text-left px-3 py-2 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Website</th>
                                    <td className="px-3 py-2" colSpan={3}>
                                        <a href={firm.website.startsWith("http") ? firm.website : `https://${firm.website}`} target="_blank" rel="noreferrer" className="text-amber-500 hover:text-amber-400 inline-flex items-center gap-1">
                                            {firm.website} <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </header>

            {/* Overview + sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2 border border-zinc-800 bg-zinc-950 p-6 space-y-5">
                    <EditableField label="Overview" value={firm.overview} onSave={(v) => updateFirm({ overview: v })} multiline testid="firm-overview" />
                    <EditableField label="My notes" value={firm.notes} onSave={(v) => updateFirm({ notes: v })} multiline testid="firm-notes" />

                    {firm.custom_fields && Object.keys(firm.custom_fields).length > 0 && (
                        <div data-testid="firm-custom-fields">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-2">Pattern fields</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {Object.entries(firm.custom_fields).map(([k, v]) => (
                                    <div key={k} className="border border-zinc-800 p-3 bg-black/40">
                                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{k}</div>
                                        <div className="text-sm text-zinc-200">{Array.isArray(v) ? v.join(", ") : String(v)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Recent news</div>
                        <ul className="space-y-2">
                            {(firm.recent_news || []).map((n, i) => (
                                <li key={i} className="text-sm text-zinc-300 border-l-2 border-zinc-800 pl-3">{n}</li>
                            ))}
                            {!firm.recent_news?.length && <li className="text-xs text-zinc-600">No news yet</li>}
                        </ul>
                    </div>
                </div>
                <div className="border border-zinc-800 bg-zinc-950 p-6 space-y-5">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Competitors</div>
                        <div className="flex flex-wrap gap-1.5">
                            {(firm.competitors || []).map((c) => (
                                <span key={c} className="border border-zinc-700 bg-zinc-900/50 text-zinc-300 text-xs px-2 py-0.5">{c}</span>
                            ))}
                            {!firm.competitors?.length && <span className="text-xs text-zinc-600">None tracked</span>}
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Tags</div>
                        <div className="flex flex-wrap gap-1.5">
                            {(firm.tags || []).map((t) => (
                                <span key={t} className="border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs px-2 py-0.5">{t}</span>
                            ))}
                            {!firm.tags?.length && <span className="text-xs text-zinc-600">None</span>}
                        </div>
                    </div>

                    {pieData.some(d => d.value > 0) && (
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Revenue mix</div>
                            <div style={{ width: "100%", height: 180, minWidth: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={pieData} dataKey="value" innerRadius={45} outerRadius={75} stroke="#000" strokeWidth={1}>
                                            {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }} formatter={(v) => fmtMoney(v)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Competitor radar — heuristic visual; hidden when no competitors tracked */}
            <CompetitorRadar firm={firm} />

            {/* Products */}
            <section className="border border-zinc-800 bg-zinc-950" data-testid="products-section">
                <div className="flex items-center justify-between p-5 border-b border-zinc-800">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-1">// Money loop</div>
                        <div className="font-heading font-bold text-xl text-zinc-50">Products</div>
                    </div>
                    <button onClick={openAddProduct} data-testid="add-product-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
                        <Plus className="w-3.5 h-3.5" /> Add product
                    </button>
                </div>

                {firm.products?.length ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                        <div className="lg:col-span-1 border-r border-zinc-800">
                            {firm.products.map((p) => (
                                <button key={p.id} onClick={() => setActiveProductId(p.id)} data-testid={`product-item-${p.id}`}
                                    style={activeProduct?.id === p.id && firm.color ? { borderLeftColor: firm.color } : undefined}
                                    className={`w-full text-left p-4 border-b border-zinc-800/60 transition-colors ${
                                        activeProduct?.id === p.id ? "bg-zinc-900/60 border-l-2 " + (firm.color ? "" : "border-l-amber-500") : "hover:bg-zinc-900/40 border-l-2 border-l-transparent"
                                    }`}>
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="font-heading font-bold text-sm text-zinc-50 flex items-center gap-1.5">
                                            {firm.color && <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: firm.color }} />}
                                            {p.name}
                                        </div>
                                        <div className={`font-mono text-[11px] ${p.growth_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                                            {p.growth_pct >= 0 ? "+" : ""}{Number(p.growth_pct).toFixed(1)}%
                                        </div>
                                    </div>
                                    <div className="font-mono text-xs text-amber-500">{fmtMoney(p.annual_revenue_usd)}</div>
                                    <div className="text-[10px] text-zinc-500 mt-1 truncate">{p.description || "—"}</div>
                                </button>
                            ))}
                        </div>

                        <div className="lg:col-span-2 p-6">
                            {activeProduct && (
                                <>
                                    <div className="flex items-start justify-between mb-5">
                                        <div>
                                            <div className="font-heading font-black text-2xl text-zinc-50">{activeProduct.name}</div>
                                            <div className="text-sm text-zinc-400 mt-1 max-w-xl">{activeProduct.description || "No description."}</div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => openEditProduct(activeProduct)} data-testid={`edit-product-${activeProduct.id}`} className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-widest text-zinc-400 px-3 py-2 transition-colors">
                                                Edit
                                            </button>
                                            <button onClick={() => deleteProduct(activeProduct.id)} data-testid={`delete-product-${activeProduct.id}`} className="border border-zinc-800 hover:border-red-700 hover:text-red-400 text-xs font-mono uppercase tracking-widest text-zinc-500 px-3 py-2 transition-colors">
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                        {[
                                            { l: "Annual revenue", v: fmtMoney(activeProduct.annual_revenue_usd) },
                                            { l: "Units sold", v: activeProduct.units_sold?.toLocaleString() || "0" },
                                            { l: "YoY growth", v: `${activeProduct.growth_pct >= 0 ? "+" : ""}${Number(activeProduct.growth_pct).toFixed(1)}%`, tone: activeProduct.growth_pct >= 0 ? "text-emerald-400" : "text-red-400" },
                                            { l: "Firm share", v: `${Number(activeProduct.revenue_share_pct).toFixed(0)}%` },
                                        ].map((s) => (
                                            <div key={s.l} className="border border-zinc-800 p-3 bg-black/40">
                                                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{s.l}</div>
                                                <div className={`font-heading font-bold text-lg ${s.tone || "text-zinc-50"}`}>{s.v}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="border border-zinc-800 p-4 bg-black/40">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Revenue · 12 months</div>
                                        <div style={{ width: "100%", height: 220, minWidth: 0 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={activeProduct.monthly_revenue || []}>
                                                    <XAxis dataKey="month" stroke="#52525B" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#fafafa" }} />
                                                    <YAxis stroke="#52525B" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#fafafa" }} tickFormatter={fmtMoney} width={60} />
                                                    <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11, color: "#fafafa" }} formatter={(v) => fmtMoney(v)} />
                                                    <Line type="monotone" dataKey="revenue" stroke="#F59E0B" strokeWidth={2} dot={{ fill: "#F59E0B", r: 2 }} activeDot={{ r: 4 }} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Pricing</div>
                                            <div className="font-mono text-zinc-200">{activeProduct.pricing || "—"}</div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <div className="font-heading font-bold text-zinc-300 text-lg mb-2">No products yet</div>
                        <div className="text-sm text-zinc-500 mb-5">Add the firm's products to map the money loop.</div>
                        <button onClick={openAddProduct} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5 inline-flex items-center gap-2">
                            <Plus className="w-3.5 h-3.5" /> Add product
                        </button>
                    </div>
                )}
            </section>

            <SuggestedPartners firmId={firm.id} firmName={firm.name} />

            {/* Modal */}
            {showProductModal && productDraft && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="product-modal">
                    <div className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl">
                        <div className="flex items-center justify-between border-b border-zinc-800 p-4">
                            <div className="font-heading font-bold text-zinc-50">{productDraft.id ? "Edit product" : "Add product"}</div>
                            <button onClick={() => setShowProductModal(false)} className="text-zinc-500 hover:text-zinc-100" data-testid="close-product-modal"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                            {[
                                { k: "name", l: "Name", type: "text" },
                                { k: "description", l: "Description", type: "textarea" },
                                { k: "pricing", l: "Pricing", type: "text" },
                                { k: "annual_revenue_usd", l: "Annual revenue (USD)", type: "number" },
                                { k: "units_sold", l: "Units sold", type: "number" },
                                { k: "growth_pct", l: "Growth %", type: "number" },
                                { k: "revenue_share_pct", l: "Revenue share %", type: "number" },
                            ].map(({ k, l, type }) => (
                                <div key={k}>
                                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">{l}</label>
                                    {type === "textarea" ? (
                                        <textarea
                                            value={productDraft[k] || ""}
                                            onChange={(e) => setProductDraft({ ...productDraft, [k]: e.target.value })}
                                            rows={3}
                                            data-testid={`product-field-${k}`}
                                            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500"
                                        />
                                    ) : (
                                        <input
                                            type={type}
                                            value={productDraft[k] ?? ""}
                                            onChange={(e) => setProductDraft({ ...productDraft, [k]: type === "number" ? e.target.value : e.target.value })}
                                            data-testid={`product-field-${k}`}
                                            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500 font-mono"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2 border-t border-zinc-800 p-4">
                            <button onClick={() => setShowProductModal(false)} className="border border-zinc-800 hover:border-zinc-600 text-xs font-mono uppercase tracking-widest text-zinc-400 px-4 py-2">
                                Cancel
                            </button>
                            <button onClick={saveProduct} data-testid="save-product-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2 inline-flex items-center gap-2">
                                <Save className="w-3.5 h-3.5" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


const CATEGORY_TONE = {
    customer: "border-blue-500/40 bg-blue-500/10 text-blue-300",
    partner: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    integration: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    acquirer: "border-purple-500/40 bg-purple-500/10 text-purple-300",
    reseller: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
};

function SuggestedPartners({ firmId, firmName }) {
    const nav = useNavigate();
    const [suggestions, setSuggestions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [researchingName, setResearchingName] = useState("");
    // Bulk save state for the "Save top N" action — researches up to N suggestions sequentially,
    // navigating to the LAST saved one when finished.
    const [bulk, setBulk] = useState({ active: false, current: 0, total: 0 });
    const [savedIds, setSavedIds] = useState({}); // name -> newFirmId (so we can show "View ✓")
    const [synergyStatus, setSynergyStatus] = useState({}); // name -> 'queued' | 'ready' | 'failed'

    const load = async () => {
        setLoading(true); setError(""); setSuggestions(null);
        try {
            const { data } = await api.post(`/firms/${firmId}/suggest-partners`);
            setSuggestions(data.suggestions || []);
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setError(rl || (typeof d === "string" ? d : "Failed to suggest partners"));
        } finally {
            setLoading(false);
        }
    };

    // Kick off a deep-research job for a suggested company name, save it as a firm, AND fire-and-forget a
    // synergy/best (source × new) so the Kanban card / pitch deck are brewing by the time the user clicks View.
    // Returns the new firm id on success, null on failure.
    const researchSuggestion = async (name, { navigateOnSuccess = true } = {}) => {
        setResearchingName(name);
        try {
            const { data: enqueued } = await api.post("/research", { firm_name: name });
            const dossier = await pollJob(enqueued.job_id);
            const { data: created } = await api.post("/firms", dossier);
            setSavedIds((s) => ({ ...s, [name]: created.id }));
            setSynergyStatus((s) => ({ ...s, [name]: "queued" }));
            // Fire-and-forget synergy/best. Don't block the loop. Source firm is the one whose detail page we're on.
            api.post("/synergy/best", { firm_a_id: firmId, firm_b_id: created.id })
                .then(async ({ data: enq }) => {
                    try {
                        await pollJob(enq.job_id, { timeoutMs: 4 * 60 * 1000 });
                        setSynergyStatus((s) => ({ ...s, [name]: "ready" }));
                    } catch {
                        setSynergyStatus((s) => ({ ...s, [name]: "failed" }));
                    }
                })
                .catch(() => setSynergyStatus((s) => ({ ...s, [name]: "failed" })));
            if (navigateOnSuccess) nav(`/app/firms/${created.id}`);
            return created.id;
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setError(rl || (typeof d === "string" ? d : `Could not research ${name}`));
            return null;
        } finally {
            setResearchingName("");
        }
    };

    // Sequentially research the top N un-saved suggestions. Stays on this page so the user can keep working.
    const bulkSaveTopN = async (n = 5) => {
        if (!suggestions || suggestions.length === 0 || bulk.active) return;
        const targets = suggestions.filter((s) => !savedIds[s.name]).slice(0, n);
        if (targets.length === 0) return;
        setError("");
        setBulk({ active: true, current: 0, total: targets.length });
        for (let i = 0; i < targets.length; i++) {
            setBulk({ active: true, current: i + 1, total: targets.length });
            const id = await researchSuggestion(targets[i].name, { navigateOnSuccess: false });
            if (!id) break; // short-circuit on first failure
        }
        setBulk({ active: false, current: 0, total: 0 });
    };

    return (
        <section className="border border-zinc-800 bg-zinc-950 mb-6" data-testid="suggested-partners-section">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                        Suggested external partners for <span className="text-zinc-50">{firmName}</span>
                    </div>
                </div>
                <button
                    onClick={load}
                    disabled={loading || !!researchingName}
                    data-testid="suggest-partners-btn"
                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-1.5 transition-colors disabled:opacity-50"
                >
                    <Sparkles className="w-3 h-3" /> {loading ? "Thinking…" : suggestions ? "Refresh" : "Suggest 10"}
                </button>
            </div>

            {error && <div className="m-4 border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-3 py-2 font-mono" data-testid="suggest-error">{error}</div>}

            {!suggestions && !loading && !error && (
                <div className="px-5 py-8 text-center text-xs font-mono text-zinc-600">
                    Click <span className="text-amber-400">Suggest 10</span> — Claude proposes real firms outside your library that would want {firmName}'s products.
                </div>
            )}
            {loading && <div className="p-8"><Loader label={`Finding partners for ${firmName}`} /></div>}

            {suggestions && suggestions.length > 0 && (
                <>
                    {/* Bulk save banner */}
                    {(() => {
                        const unsaved = suggestions.filter((s) => !savedIds[s.name]);
                        if (unsaved.length === 0) return null;
                        const n = Math.min(5, unsaved.length);
                        return (
                            <div className="border-b border-amber-500/40 bg-amber-500/5 px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="suggest-bulk-banner">
                                <div className="text-xs text-zinc-300 font-mono">
                                    {bulk.active ? (
                                        <><span className="text-amber-400">Saving</span> {bulk.current}/{bulk.total} — researching with Claude (one at a time)…</>
                                    ) : (
                                        <>Promote the top {n} {n === 1 ? "suggestion" : "suggestions"} to your library in one batch. Each gets a full Claude dossier.</>
                                    )}
                                </div>
                                <button
                                    onClick={() => bulkSaveTopN(5)}
                                    disabled={bulk.active || !!researchingName}
                                    data-testid="suggest-bulk-save-btn"
                                    className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-wait text-black font-semibold text-[11px] uppercase tracking-wider px-3 py-1.5"
                                >
                                    <Plus className="w-3.5 h-3.5" /> {bulk.active ? `Saving ${bulk.current}/${bulk.total}…` : `Save top ${n} to library`}
                                </button>
                            </div>
                        );
                    })()}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="suggestions-table">
                        <thead>
                            <tr className="border-b border-zinc-800 text-left">
                                <th className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Company</th>
                                <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Industry · Region</th>
                                <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Why</th>
                                <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Category</th>
                                <th className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {suggestions.map((s, i) => {
                                const tone = CATEGORY_TONE[s.category] || CATEGORY_TONE.partner;
                                const isResearching = researchingName === s.name;
                                const savedId = savedIds[s.name];
                                return (
                                    <tr key={s.name + i} className="border-b border-zinc-800/60 hover:bg-zinc-900/30 align-top" data-testid={`suggestion-row-${i}`}>
                                        <td className="px-5 py-3 text-zinc-50 font-semibold">{s.name}</td>
                                        <td className="px-3 py-3 font-mono text-xs text-zinc-400">{s.industry || "—"}<br />{s.region || "—"}</td>
                                        <td className="px-3 py-3 text-zinc-300 text-xs leading-relaxed max-w-md">{s.reason}</td>
                                        <td className="px-3 py-3"><span className={`inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tone}`}>{s.category}</span></td>
                                        <td className="px-5 py-3 text-right">
                                            {savedId ? (() => {
                                                const ss = synergyStatus[s.name];
                                                const tone = ss === "ready"
                                                    ? "text-emerald-400 hover:text-emerald-300"
                                                    : ss === "failed"
                                                    ? "text-zinc-500 hover:text-zinc-400"
                                                    : "text-amber-400 hover:text-amber-300";
                                                const label = ss === "ready"
                                                    ? "🤝 synergy ready"
                                                    : ss === "failed"
                                                    ? "synergy off"
                                                    : "synergy brewing…";
                                                return (
                                                    <div className="flex flex-col items-end gap-0.5">
                                                        <button
                                                            onClick={() => nav(`/app/firms/${savedId}`)}
                                                            data-testid={`suggestion-view-${i}`}
                                                            className={`inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider ${tone}`}
                                                        >
                                                            <Check className="w-3 h-3" /> View
                                                        </button>
                                                        <span className={`font-mono text-[9px] tracking-wider ${tone}`} data-testid={`suggestion-synergy-status-${i}`}>
                                                            {label}
                                                        </span>
                                                    </div>
                                                );
                                            })() : (
                                                <button
                                                    onClick={() => researchSuggestion(s.name)}
                                                    disabled={!!researchingName || bulk.active}
                                                    data-testid={`research-suggestion-${i}`}
                                                    className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-300 disabled:opacity-40 disabled:cursor-wait"
                                                >
                                                    <Search className="w-3 h-3" /> {isResearching ? "Researching…" : "Research"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                </>
            )}
            {suggestions && suggestions.length === 0 && (
                <div className="px-5 py-8 text-center text-xs font-mono text-zinc-600">AI returned no suggestions. Try refreshing.</div>
            )}
        </section>
    );
}
